import { useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { Badge, Button, Card, ConfirmDialog, Modal, SearchSelect, Textarea } from '@doccareplus/ui';
import { selectUser, showToast, toastApiError, useAppSelector } from '@doccareplus/kernel/application';
import { profileInitials, profileName, profileSubtitle, relationshipKey } from '../domain/profiles';
import type { PortalDisconnectedProfileDto, PortalFoundProfileDto, PortalProfileDto, PortalRelationship } from '../infrastructure/portalProfiles.api';
import { useDisconnectedProfilesQuery, useFoundProfilesQuery, usePortalProfilesQuery } from '../application/profiles.query';
import {
  useClaimProfileCommand,
  useDisconnectProfileCommand,
  useRequestDetailCorrectionCommand,
  useSelectProfileCommand,
} from '../application/profiles.command';
import { ProfilesListSkeleton } from './ProfilesSkeletons';

const RELATIONSHIPS: PortalRelationship[] = ['Self', 'Parent', 'Guardian', 'Caregiver'];

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-gradient-to-br from-[var(--brand-500)] to-[var(--brand-700)] text-[12.5px] font-bold text-white">
      {profileInitials(name)}
    </span>
  );
}

function ClaimedRow({ profile }: { profile: PortalProfileDto }) {
  const { t } = useTranslation('portal');
  const select = useSelectProfileCommand();
  const disconnect = useDisconnectProfileCommand();
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  return (
    <Card className={profile.isActiveSelection ? 'border-[var(--primary)] p-4' : 'p-4'}>
      <div className="flex items-center gap-3">
        <Avatar name={profileName(profile)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[14px] font-bold">{profileName(profile)}</span>
            <span className="rounded-full bg-[var(--primary-weak)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--primary)]">
              {t(relationshipKey(profile.relationshipType), profile.relationshipType)}
            </span>
            {profile.isArchived && (
              <Badge variant="default">
                {t('profile.archived', 'Archived · read-only')}
              </Badge>
            )}
          </div>
          <div className="truncate text-[12.5px] text-[var(--text-2)]">{profile.clinicName}</div>
          <div className="text-[11.5px] text-[var(--text-3)]">{profileSubtitle(profile)}</div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {profile.isActiveSelection ? (
          <div className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-[var(--primary)] text-[12px] font-bold text-[var(--primary)]">
            <span aria-hidden="true">✓</span>
            {t('profile.viewing', 'Viewing')}
          </div>
        ) : (
          <Button variant="outline" size="sm" fullWidth isLoading={select.isPending} onClick={() => select.mutate(profile.linkId)}>
            {t('profile.view', 'View')}
          </Button>
        )}
        <Button variant="ghost" size="sm" fullWidth isLoading={disconnect.isPending} onClick={() => setConfirmingRemove(true)}>
          {t('profile.disconnect', 'Disconnect')}
        </Button>
      </div>

      {confirmingRemove && (
        <ConfirmDialog
          title={t('profile.removeConfirmTitle', 'Remove this profile?')}
          description={t('profile.removeConfirm', 'Remove access to this profile? You will need to verify again to reconnect.')}
          confirmLabel={t('profile.remove', 'Remove')}
          cancelLabel={t('profile.keepConnected', 'Keep connected')}
          isLoading={disconnect.isPending}
          onCancel={() => setConfirmingRemove(false)}
          onConfirm={() => {
            disconnect.mutate(profile.linkId, { onSuccess: () => setConfirmingRemove(false) });
          }}
        />
      )}
    </Card>
  );
}

function FoundRow({ profile }: { profile: PortalFoundProfileDto }) {
  const { t } = useTranslation('portal');
  const claim = useClaimProfileCommand();
  const [relationship, setRelationship] = useState<PortalRelationship>('Self');
  const relationshipOptions = RELATIONSHIPS.map((value) => ({
    value,
    label: t(relationshipKey(value), value),
  }));

  return (
    <Card className="flex flex-col gap-3 border-dashed p-4">
      <div className="flex items-center gap-3">
        <Avatar name={profile.patientDisplayName} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-bold">{profile.patientDisplayName}</div>
          <div className="truncate text-[12.5px] text-[var(--text-2)]">{profileSubtitle(profile)}</div>
          {(profile.maskedPhone || profile.maskedEmail) && (
            <div className="text-[11.5px] text-[var(--text-3)]">
              {t('profile.matchedContact', 'Matched contact')} · {profile.maskedPhone ?? profile.maskedEmail}
            </div>
          )}
        </div>
      </div>
      <SearchSelect
        label={t('profile.relationshipLabel', 'Relationship')}
        options={relationshipOptions}
        value={relationship}
        onChange={(value) => setRelationship(value as PortalRelationship)}
        searchable={false}
        appearance="input"
        triggerAriaLabel={t('profile.relationshipLabel', 'Relationship')}
      />
      <Button
        variant="primary"
        size="sm"
        fullWidth
        isLoading={claim.isPending}
        onClick={() => claim.mutate({ patientId: profile.patientId, relationshipType: relationship })}
      >
        {t('profile.claim', 'Connect this profile')}
      </Button>
    </Card>
  );
}

function DisconnectedRow({ profile }: { profile: PortalDisconnectedProfileDto }) {
  const { t } = useTranslation('portal');
  return (
    <Card className="flex items-center gap-3 p-3.5">
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold">
          {profileName(profile)} · {profile.clinicName}
        </div>
        <div className="text-[11.5px] text-[var(--text-3)]">
          {new Date(profile.disconnectedAt).toLocaleDateString()}
        </div>
      </div>
      <span className="whitespace-nowrap rounded-full bg-[var(--surface-3)] px-2.5 py-1 text-[11px] font-bold text-[var(--text-2)]">
        {t('profile.disconnectedPill', 'Disconnected')}
      </span>
    </Card>
  );
}

function PortalAccountSection() {
  const { t } = useTranslation('portal');
  const user = useAppSelector(selectUser);

  if (!user) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[15px] font-bold">{t('profile.account', 'Portal account')}</h2>
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[15px] font-bold text-[var(--primary)]">
            {profileInitials(`${user.firstName} ${user.lastName}`)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[14.5px] font-bold">{`${user.firstName} ${user.lastName}`.trim()}</div>
            <div className="truncate text-[12px] text-[var(--text-3)]">{user.email}</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {user.isEmailVerified && <Badge variant="success">{t('profile.emailVerified', 'Email verified')}</Badge>}
          {user.isPhoneVerified && <Badge variant="success">{t('profile.phoneVerified', 'Phone verified')}</Badge>}
        </div>
      </Card>
    </section>
  );
}

function RequestCorrectionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('portal');
  const requestCorrection = useRequestDetailCorrectionCommand();
  const [note, setNote] = useState('');

  return (
    <Modal
      title={t('profile.correction.title', 'Request a correction')}
      description={t(
        'profile.correction.description',
        'Tell the clinic what needs fixing on your record. Staff review and apply the change.'
      )}
      onClose={onClose}
      footer={
        <Button
          fullWidth
          isLoading={requestCorrection.isPending}
          disabled={!note.trim() || requestCorrection.isPending}
          onClick={() =>
            requestCorrection.mutate(
              { note: note.trim() },
              {
                onSuccess: () => {
                  showToast({ type: 'success', message: t('profile.correction.sent', 'Correction request sent') });
                  onClose();
                },
                onError: (error) => toastApiError(error, t('profile.correction.failed', 'Could not send the request')),
              }
            )
          }
        >
          {t('profile.correction.submit', 'Send request')}
        </Button>
      }
    >
      <Textarea
        label={t('profile.correction.noteLabel', "What's wrong?")}
        placeholder={t('profile.correction.notePlaceholder', 'e.g. My date of birth is incorrect')}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        rows={4}
      />
    </Modal>
  );
}

function PatientDetailsSection({ profile }: { profile: PortalProfileDto }) {
  const { t } = useTranslation('portal');
  const [requesting, setRequesting] = useState(false);

  const rows: Array<[string, string]> = [
    [t('profile.details.name', 'Name'), profile.patientDisplayName],
    [t('profile.details.relationship', 'Relationship'), t(relationshipKey(profile.relationshipType), profile.relationshipType)],
    ...(profile.dobYear ? ([[t('profile.details.dobYear', 'Birth year'), String(profile.dobYear)]] as Array<[string, string]>) : []),
    ...(profile.maskedPhone ? ([[t('profile.details.phone', 'Phone'), profile.maskedPhone]] as Array<[string, string]>) : []),
    ...(profile.maskedEmail ? ([[t('profile.details.email', 'Email'), profile.maskedEmail]] as Array<[string, string]>) : []),
  ];

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[15px] font-bold">{t('profile.details.title', 'Patient details')} — {profileName(profile)}</h2>
      <Card className="p-4">
        {rows.map(([label, value], index) => (
          <div
            key={label}
            className={`flex items-center justify-between gap-3 py-2.5 text-[13px] ${index > 0 ? 'border-t border-[var(--border)]' : ''}`}
          >
            <span className="text-[var(--text-3)]">{label}</span>
            <span className="font-semibold">{value}</span>
          </div>
        ))}
        <p className="mt-2.5 text-[12px] leading-5 text-[var(--text-3)]">
          {t(
            'profile.details.hint',
            "These details belong to the clinic's record — staff review and apply requested corrections."
          )}
        </p>
        <Button variant="outline" fullWidth className="mt-3" onClick={() => setRequesting(true)}>
          {t('profile.correction.button', 'Request a correction')}
        </Button>
      </Card>

      {requesting && <RequestCorrectionModal onClose={() => setRequesting(false)} />}
    </section>
  );
}

export function ProfilesPage() {
  const { t } = useTranslation('portal');
  const profiles = usePortalProfilesQuery();
  const found = useFoundProfilesQuery();
  const disconnected = useDisconnectedProfilesQuery();
  const activeProfile = profiles.data?.profiles.find((p) => p.isActiveSelection) ?? null;

  return (
    <div className="mx-auto flex max-w-[860px] flex-col gap-7">
      <div className="md:hidden">
        <h1 className="text-[23px] font-bold tracking-[-0.01em]">{t('profile.title', 'Profiles & access')}</h1>
        <p className="mt-1 text-[12.5px] text-[var(--text-3)]">
          {t('profile.subtitle', 'Linked patient records for this account')}
        </p>
      </div>

      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-[15px] font-bold">{t('profile.yourProfiles', 'Linked profiles')}</h2>
          <p className="mt-1 text-[12.5px] leading-5 text-[var(--text-2)]">
            {t('profile.linkedHint', 'Choose the patient and clinic record you want to view.')}
          </p>
        </div>
        {profiles.isLoading ? (
          <ProfilesListSkeleton label="Loading linked profiles" />
        ) : profiles.data && profiles.data.profiles.length > 0 ? (
          profiles.data.profiles.map((p) => <ClaimedRow key={p.linkId} profile={p} />)
        ) : (
          <p className="rounded-[14px] border border-dashed border-[var(--border-strong)] p-4 text-center text-[13px] text-[var(--text-3)]">
            {t('profile.noneClaimed', 'No profiles connected yet. Connect one below.')}
          </p>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-[15px] font-bold">{t('profile.available', 'Profiles found for you')}</h2>
          <p className="mt-1 text-[12.5px] leading-5 text-[var(--text-2)]">
            {t('profile.foundHint', 'Matched on your verified phone or email. Details stay masked until you connect.')}
          </p>
        </div>
        {found.isLoading ? (
          <ProfilesListSkeleton label="Loading found profiles" rows={2} />
        ) : found.data && found.data.profiles.length > 0 ? (
          found.data.profiles.map((p) => <FoundRow key={p.patientId} profile={p} />)
        ) : (
          <p className="rounded-[14px] border border-dashed border-[var(--border-strong)] p-4 text-center text-[13px] text-[var(--text-3)]">
            {t('profile.noneFound', 'No profiles found for your number yet. Ask your clinic to add your details.')}
          </p>
        )}
      </section>

      <Card className="p-4 text-[12px] leading-5 text-[var(--text-3)]">
        {t(
          'profile.disconnectHint',
          'Disconnecting hides that chart from this account. The clinic keeps its record, and reconnecting requires a fresh verified match.'
        )}
      </Card>

      {!disconnected.isLoading && (disconnected.data?.profiles.length ?? 0) > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[15px] font-bold">{t('profile.disconnectedAccess', 'Disconnected access')}</h2>
          {disconnected.data!.profiles.map((p) => (
            <DisconnectedRow key={p.linkId} profile={p} />
          ))}
        </section>
      )}

      <PortalAccountSection />

      {activeProfile && <PatientDetailsSection profile={activeProfile} />}
    </div>
  );
}
